/**
 * REST API adapters for Maintenance component
 * Transforms REST responses to match GraphQL structure
 *
 * Usage: Replace import:
 * - import { useMaintenanceQuery } from './queries';
 * + import { useMaintenanceQuery } from './queries.rest';
 */
import { useMemo } from 'react';

import { useSiteConfig } from '@hooks/queries';

import type { MaintenanceQuery } from './gqlTypes/MaintenanceQuery';

/**
 * REST adapter for useMaintenanceQuery
 * Matches Apollo useQuery interface with skip support
 */
export const useMaintenanceQuery = (skip: boolean) => {
  const { data, isLoading, error, refetch } = useSiteConfig();

  // Transform to GraphQL format
  const transformedData: MaintenanceQuery | undefined = useMemo(() => {
    if (skip || !data) return undefined;

    return {
      site: {
        __typename: 'Site' as const,
        maintenanceMode: data.maintenance_mode ?? false,
        maintenanceAllowed: true, // IP-based check not implemented in REST yet
      },
    };
  }, [skip, data]);

  return {
    data: transformedData,
    loading: !skip && isLoading,
    error: error ? new Error(String(error)) : undefined,
    refetch: async () => {
      if (!skip) {
        await refetch();
      }
    },
  };
};
